/**
 * Instant ↔ `datetime-local` conversion for the Broadcasts composer.
 *
 * Its own module because BOTH `broadcastPayload.js` (which serializes a
 * schedule for the wire) and `broadcastDraft.js` (which restores one into the
 * form) need it, and importing one from the other would make a cycle.
 *
 * PURE: no ambient clock, no locale assumptions beyond the browser's own zone.
 */

/**
 * Convert a `datetime-local` value ("2026-08-09T14:30", local wall time, no
 * offset) into an unambiguous instant.
 *
 * @param {string} local
 * @returns {string|null} ISO-8601 UTC, or null when absent/unparseable.
 */
export const toScheduledAtIso = (local) => {
  if (!local) return null;
  const when = new Date(local); // parsed in the browser's local zone
  return Number.isNaN(when.getTime()) ? null : when.toISOString();
};

/**
 * ISO instant → the `datetime-local` value that renders it in local time.
 *
 * @param {string|null} iso
 * @returns {string} "" when absent or unparseable.
 */
export const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  return (
    `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}` +
    `T${pad(when.getHours())}:${pad(when.getMinutes())}`
  );
};

/**
 * A calendar date as `YYYY-MM-DD` in the browser's LOCAL zone.
 *
 * Deliberately not `toISOString().slice(0, 10)`, which reads the UTC calendar.
 * Mixing the two is a real bug: `setDate`/`getDate` walk the local calendar, so
 * formatting the result as UTC shifts the whole window by a day for anyone
 * whose offset straddles midnight UTC — an evening in the Americas, an early
 * morning in Asia. "Last 7 days" then silently means a different seven days
 * than the label promises.
 *
 * @param {Date} date
 * @returns {string}
 */
export const toIsoDate = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

function pad(n) {
  return String(n).padStart(2, "0");
}
